import React, { useEffect, useMemo, useState } from "react";
import { X, ChevronsLeft, ChevronsRight, Smile, Frown, Meh, Newspaper, Flame } from "lucide-react";
import { apiClient } from "../lib/api";
import { titleCase, formatDateID } from "../lib/format";

const SENTIMENT_COLOR = {
  positive: "#10b981",
  neutral: "#facc15",
  negative: "#ef4444",
};

const statusToColor = (s) => {
  const t = (s || "").toLowerCase();
  if (t === "kritis") return "#ef4444";
  if (t === "tinggi") return "#f59e0b";
  if (t === "sedang") return "#facc15";
  return "#10b981";
};

export const SentimentAnalysisModal = ({ open, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("all");
  const [selectedProvince, setSelectedProvince] = useState("ALL");
  const [selectedIssue, setSelectedIssue] = useState("ALL");
  const [collapseIssues, setCollapseIssues] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    // Use proxy w/ limit=500 to fetch all 425 news
    apiClient
      .get(`/proxy/get_sentiment_news?sentiment=${filter}&limit=500`)
      .then((r) => setData(r.data?.data || null))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [open, filter]);

  // Reset issue selection when province changes
  useEffect(() => { setSelectedIssue("ALL"); }, [selectedProvince]);

  const provinces = useMemo(() => data?.provinces || [], [data]);
  const allIssues = useMemo(() => data?.issues || [], [data]);
  const allNews = useMemo(() => data?.news || [], [data]);

  // Filter issues by selected province
  const filteredIssues = useMemo(() => {
    if (selectedProvince === "ALL") return allIssues;
    return allIssues.filter((i) => (i.province || "").toUpperCase() === selectedProvince);
  }, [allIssues, selectedProvince]);

  // Build a set of news_ids from filtered issues — used as province-level news filter
  const provNewsIds = useMemo(() => {
    if (selectedProvince === "ALL") return null;
    const ids = new Set();
    filteredIssues.forEach((i) => (i.news_idx || []).forEach((nid) => ids.add(nid)));
    return ids;
  }, [filteredIssues, selectedProvince]);

  // Final filtered news for display
  const filteredNews = useMemo(() => {
    let n = allNews;
    if (selectedIssue !== "ALL") {
      const iss = allIssues.find((i) => i.title === selectedIssue);
      if (iss) {
        const ids = new Set(iss.news_idx || []);
        n = allNews.filter((a) => ids.has(a.news_id));
      }
    } else if (provNewsIds) {
      n = allNews.filter((a) => provNewsIds.has(a.news_id));
    }
    return n;
  }, [allNews, selectedIssue, allIssues, provNewsIds]);

  // Recompute counts based on filtered news (not stats from API which is global)
  const counts = useMemo(() => {
    const c = { positive: 0, neutral: 0, negative: 0 };
    filteredNews.forEach((n) => {
      if (n.sentiment === "positive") c.positive++;
      else if (n.sentiment === "negative") c.negative++;
      else c.neutral++;
    });
    return c;
  }, [filteredNews]);

  const total = (counts.positive || 0) + (counts.neutral || 0) + (counts.negative || 0) || 1;
  const pct = (v) => ((v / total) * 100).toFixed(1);

  // Province pool size: total news that match province issues
  const provincePool = useMemo(() => {
    if (selectedProvince === "ALL") return allNews.length;
    return provNewsIds ? Array.from(provNewsIds).filter((id) => allNews.find((a) => a.news_id === id)).length : 0;
  }, [allNews, provNewsIds, selectedProvince]);

  const issuePool = selectedIssue !== "ALL" ? filteredNews.length : provincePool;

  if (!open) return null;

  return (
    <div className="nms-modal-backdrop full-modal" data-testid="sentiment-modal" onClick={onClose}>
      <div className="nms-modal sentiment-modal" onClick={(e) => e.stopPropagation()}>
        <header className="sa-head">
          <div className="sa-head-left">
            <div className="sa-logo"><div className="sa-logo-ring" /></div>
            <h2 className="sa-title">
              SENTIMENT ANALYSIS <span className="sa-tag">· {filter.toUpperCase()}</span>
            </h2>
          </div>
          <div className="sa-filters">
            <button type="button" className={`sa-filter ${filter === "all" ? "active all" : ""}`} onClick={() => setFilter("all")} data-testid="sa-filter-all">
              <span className="dot-rainbow" /> ALL
            </button>
            <button type="button" className={`sa-filter ${filter === "positive" ? "active pos" : ""}`} onClick={() => setFilter("positive")} data-testid="sa-filter-positive">
              <Smile size={11} /> POSITIVE
            </button>
            <button type="button" className={`sa-filter ${filter === "negative" ? "active neg" : ""}`} onClick={() => setFilter("negative")} data-testid="sa-filter-negative">
              <Frown size={11} /> NEGATIVE
            </button>
            <button type="button" className={`sa-filter ${filter === "neutral" ? "active neu" : ""}`} onClick={() => setFilter("neutral")} data-testid="sa-filter-neutral">
              <Meh size={11} /> NEUTRAL
            </button>
          </div>
          <div className="sa-head-right">
            <span className="sa-count" data-testid="sa-count">{filteredNews.length} / {issuePool || allNews.length} BERITA</span>
            <button className="sa-close" onClick={onClose} aria-label="Close" data-testid="sentiment-close">
              <X size={18} />
            </button>
          </div>
        </header>

        <div className="sa-body">
          <aside className="sa-prov-col">
            <div className="sa-col-head"><h3>PROVINCES</h3></div>
            <span className="sa-col-sub">Regions affected</span>
            <button type="button" className={`sa-all-btn ${selectedProvince === "ALL" ? "active" : ""}`} onClick={() => setSelectedProvince("ALL")} data-testid="sa-all-regions">
              ALL REGIONS
            </button>
            <ul className="sa-prov-list">
              {provinces.map((p, idx) => {
                const provName = p.region_name || p.name || "";
                const provCode = p.region_code || p.code || idx;
                const provStatus = p.additional_data?.level || p.status || "";
                const col = statusToColor(provStatus);
                const active = selectedProvince === provName.toUpperCase();
                return (
                  <li key={provCode}>
                    <button type="button" className={`sa-prov-item ${active ? "active" : ""}`} onClick={() => setSelectedProvince(provName.toUpperCase())} data-testid={`sa-prov-${provCode}`}>
                      <span className="sa-prov-rank">{idx + 1}</span>
                      <div className="sa-prov-info">
                        <div className="sa-prov-name">{titleCase(provName)}</div>
                        <span className="sa-prov-status" style={{ color: col, borderColor: col, background: `${col}1a` }}>
                          {provStatus.toUpperCase()}
                        </span>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          </aside>

          {!collapseIssues && (
            <aside className="sa-issue-col">
              <div className="sa-col-head">
                <h3>RELATED ISSUES</h3>
                <button className="sa-collapse" onClick={() => setCollapseIssues(true)} aria-label="Collapse"><ChevronsLeft size={14} /></button>
              </div>
              <span className="sa-col-sub" data-testid="sa-context">
                {selectedProvince === "ALL" ? "National Context" : selectedProvince}
              </span>
              <button type="button" className={`sa-all-btn ${selectedIssue === "ALL" ? "active" : ""}`} onClick={() => setSelectedIssue("ALL")} data-testid="sa-all-issues">
                SEMUA ISU
              </button>
              <ul className="sa-issue-list">
                {filteredIssues.map((it, idx) => (
                  <li key={`${it.title}-${idx}`}>
                    <button type="button" className={`sa-issue-item ${selectedIssue === it.title ? "active" : ""}`} onClick={() => setSelectedIssue(it.title)} data-testid={`sa-issue-${idx}`}>
                      <div className="sa-issue-title">{titleCase(it.title)}</div>
                      <div className="sa-issue-meta">
                        <span className="sa-i-news"><Newspaper size={10} /> {it.news_count || (it.news_idx || []).length} Berita</span>
                        <span className="sa-i-int"><Flame size={10} style={{ color: "#f59e0b" }} /><span style={{ color: "#f59e0b" }}>{(it.percentage ?? 0).toFixed(0)}%</span> Intensity</span>
                      </div>
                    </button>
                  </li>
                ))}
                {filteredIssues.length === 0 && (
                  <li><div className="anal-loading">Tidak ada isu untuk wilayah ini.</div></li>
                )}
              </ul>
            </aside>
          )}

          {collapseIssues && (
            <button className="sa-issue-collapsed" onClick={() => setCollapseIssues(false)} aria-label="Expand">
              <ChevronsRight size={16} />
              <span>RELATED ISSUES</span>
            </button>
          )}

          <section className="sa-news-col">
            <div className="sa-percent-bar">
              {counts.positive > 0 && <div className="sap pos" style={{ width: `${pct(counts.positive)}%` }}><Smile size={12} /> {pct(counts.positive)}%</div>}
              {counts.negative > 0 && <div className="sap neg" style={{ width: `${pct(counts.negative)}%` }}><Frown size={12} /> {pct(counts.negative)}%</div>}
              {counts.neutral > 0 && <div className="sap neu" style={{ width: `${pct(counts.neutral)}%` }}><Meh size={12} /> {pct(counts.neutral)}%</div>}
            </div>

            <div className="sa-news-scroll">
              {loading && <div className="anal-loading">Memuat berita…</div>}
              {!loading && filteredNews.length === 0 && (
                <div className="anal-loading">Tidak ada berita untuk filter ini.</div>
              )}
              {filteredNews.slice(0, 50).map((a, i) => (
                <article key={a.news_id} className="sa-news-card" data-testid={`sa-news-${a.news_id}`}>
                  <span className="sa-news-rank">{i + 1}</span>
                  <div className="sa-news-img">
                    <div className="sa-news-source-badge">{a.source}</div>
                  </div>
                  <div className="sa-news-body">
                    <div className="sa-news-top">
                      <span className="sa-news-date">{formatDateID(a.published_at)}</span>
                      <span className="sa-news-sentiment" style={{ color: SENTIMENT_COLOR[a.sentiment] || "#94a3b8", background: `${SENTIMENT_COLOR[a.sentiment] || "#94a3b8"}22` }}>
                        {a.sentiment}
                      </span>
                    </div>
                    <h4 className="sa-news-title">{a.title}</h4>
                    <p className="sa-news-snippet">{(a.snippet || "Sb").slice(0, 220)}</p>
                    <a href={a.url} target="_blank" rel="noopener noreferrer" className="sa-news-link">BACA SELENGKAPNYA →</a>
                  </div>
                </article>
              ))}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default SentimentAnalysisModal;
